/**
 * ReferralSourceField (Workspace v3.x) — the editable "Referral source" in the intake
 * drawer. Three states:
 *   • linked   — a Directory org/individual is linked (referral_org_id / _individual_id):
 *                show the name + an Unlink action.
 *   • searching — the Directory search (orgs AND individuals) is open; picking a row links it.
 *   • free text — the default: a plain text input for a referrer NOT in the Directory
 *                 (a brand-new one), exactly like before, plus a "Link…" toggle.
 *
 * Linking sets the structured id and clears the free text; un-/never-linked keeps the text.
 * Every answered search ends with the Directory's add or request door (DirectoryAddDoor),
 * match or not (LP-26): a listing added there links as a picked row does, and a request keeps
 * the searched name as the free text.
 * The picker reuses the Directory search (api.searchProviders) — it returns both orgs and
 * individuals (OrgSearchAutocomplete filters individuals out; here we keep both).
 *
 * `layout` (design pass 2026-07-25): 'stacked' is the original label-above-control
 * shape on the "+ New intake" form's 2-up grid; 'row' puts the field on the drawer's
 * ONE label:value grid (drawerLayout LABEL_COL), so it shares the same two left edges
 * as every other row in the drawer. Same states, same testids, same wiring.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { UpdateWorkItemInput } from '@/lib/api';
import { DIRECTORY_SEARCH_MIN_CHARS as MIN_CHARS, useDirectorySearch } from '@/lib/directorySearch';
import type { SearchResult } from '@/lib/types';
import { SearchField } from '@/components/primitives/SearchField';
import { TextAction } from './TextAction';
import { LABEL_COL } from './drawer/drawerLayout';
import { DirectoryAddDoor } from '@/components/clientRecord/DirectoryAddDoor';


export interface ReferralSourceFieldProps {
  referralText: string | null;
  referralOrgId: string | null;
  referralIndividualId: string | null;
  /** The resolved Directory name when linked (referral_org_resolved_name / _individual_name). */
  linkedName: string | null;
  onChange: (edits: UpdateWorkItemInput) => void;
  /**
   * Optional — fired with the picked Directory row when a link is made. The edit path
   * (drawer) omits it and re-derives the name from the server on refetch; the create
   * path (no row yet to refetch) uses it to show which partner was just linked.
   */
  onLink?: (row: SearchResult) => void;
  /** 'stacked' (default) = label above the control, spanning the create form's
   *  2-up grid. 'row' = the drawer's shared label:value grid. */
  layout?: 'stacked' | 'row';
}

const INPUT_CLASS = 'rounded-base border border-sage-200 px-2 py-1 text-ink';

export function ReferralSourceField({
  referralText,
  referralOrgId,
  referralIndividualId,
  linkedName,
  onChange,
  onLink,
  layout = 'stacked',
}: ReferralSourceFieldProps): ReactNode {
  const isLinked = !!(referralOrgId || referralIndividualId);
  const [searching, setSearching] = useState(false);
  const isRow = layout === 'row';

  // The Directory search takes a full-width line of its own under the
  // label row, so its text box never shares the narrow value column.
  const search = (
    <ReferralSearch
      className={isRow ? 'col-span-full mt-1.5' : 'mt-0.5'}
      onPick={(row) => {
        onChange(
          row.type === 'organization'
            ? { referral_org_id: row.id, referral_individual_id: null, referral_text: null }
            : { referral_individual_id: row.id, referral_org_id: null, referral_text: null },
        );
        onLink?.(row);
        setSearching(false);
      }}
      onCancel={() => setSearching(false)}
      onRequested={(text) => onChange({ referral_text: text })}
    />
  );

  return (
    <div
      className={
        isRow
          ? `${LABEL_COL} py-1 text-[13px]`
          : 'flex flex-col text-sage-400 col-span-2'
      }
      data-testid="drawer-referral-field"
    >
      <span className={isRow ? 'pt-1.5 text-sage-400' : undefined}>Referral source</span>
      <div className={isRow ? 'min-w-0' : 'contents'}>
      {isLinked ? (
        <span className={(isRow ? 'inline-flex' : 'mt-0.5 inline-flex') + ' items-center gap-2 flex-wrap'} data-testid="drawer-referral-linked">
          <span className="text-[10px] font-bold uppercase tracking-wide text-sage-400 bg-sage-50 rounded-pill px-1.5 py-0.5">
            {referralOrgId ? 'Org' : 'Person'}
          </span>
          <span className="font-semibold text-sage-800">{linkedName ?? 'Linked'}</span>
          <TextAction data-testid="drawer-referral-unlink" onClick={() => onChange({ referral_org_id: null, referral_individual_id: null })}>
            Unlink
          </TextAction>
        </span>
      ) : searching ? (
        isRow ? null : search
      ) : (
        <span className={(isRow ? '' : 'mt-0.5 ') + 'flex items-center gap-2'}>
          <input
            defaultValue={referralText ?? ''}
            aria-label="Referral source"
            data-testid="drawer-referral-text"
            onBlur={(e) => e.target.value !== (referralText ?? '') && onChange({ referral_text: e.target.value || null })}
            className={INPUT_CLASS + ' min-w-0 flex-1'}
          />
          <TextAction data-testid="drawer-referral-link-toggle" onClick={() => setSearching(true)}>
            Link…
          </TextAction>
        </span>
      )}
      </div>
      {isRow && !isLinked && searching && search}
    </div>
  );
}

/** Inline Directory search — orgs AND individuals. Picking a row links it; no match keeps
 *  the referrer as free text. The house Directory search (useDirectorySearch). */
function ReferralSearch({
  className,
  onPick,
  onCancel,
  onRequested,
}: {
  className: string;
  onPick: (row: SearchResult) => void;
  onCancel: () => void;
  onRequested: (text: string) => void;
}): ReactNode {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // The house Directory search: only the answer to the query as it stands.
  const { rows: results, loading } = useDirectorySearch({ q: query.trim(), enabled: true, limit: 20 });

  return (
    <div className={className}>
      <SearchField
        ref={inputRef}
        value={query}
        onChange={setQuery}
        aria-label="Search the Directory"
        placeholder="Search"
        size="compact"
        testId="drawer-referral-search"
      />
      <ul role="listbox" aria-label="Referral source results" className="mt-1 max-h-48 overflow-y-auto rounded-base border border-sage-200 bg-paper">
        {loading && <li className="px-2 py-1 text-[12px] text-sage-400">Searching…</li>}
        {!loading && query.trim().length >= MIN_CHARS && results.length === 0 && (
          <li className="px-2 py-1 text-[12px] text-sage-400">Cancel to keep it as typed.</li>
        )}
        {results.map((row) => (
          <li
            key={`${row.type}-${row.id}`}
            role="option"
            aria-selected={false}
            data-testid="drawer-referral-option"
            onMouseDown={(e) => {
              e.preventDefault();
              onPick(row);
            }}
            className="flex items-baseline gap-2 px-2 py-1 text-[13px] text-ink cursor-pointer hover:bg-sage-50"
          >
            <span className="text-[10px] font-bold uppercase tracking-wide text-sage-400 w-12 flex-shrink-0">
              {row.type === 'organization' ? 'Org' : 'Person'}
            </span>
            <span>{row.display_name}</span>
          </li>
        ))}
      </ul>
      <DirectoryAddDoor
        query={query}
        settled={!loading && query.trim().length >= MIN_CHARS}
        onAdded={onPick}
        onRequested={onRequested}
        testId="drawer-referral-door"
      />
      <span className="mt-1 flex justify-end">
        <TextAction data-testid="drawer-referral-search-cancel" onClick={onCancel}>Cancel</TextAction>
      </span>
    </div>
  );
}
